import React, { useContext, useEffect, useMemo, useReducer, useState } from "react";
import { Panel } from "../../common/components/panel/panel";
import { TableV } from "../../common/components/table/tablev2";
import { GetCompanyList, GetImportById, GetYearEndImportFiles, GetImportFileTypes, GetValidationErrorsByImportId, GetYearEndContainerTypes, GetYearEndSubmissionTypes } from "../../services/ImportDetailService";
import { Navbar, NavbarItem } from "../../common/components/navbar/navbar";
import { ProgressOverlay } from "../../common/components/progress-overlay/progress-overlay";
import { AdvancedFilter, FilterField, FilterFieldTypes, FilterValue } from "../../common/components/advanced-filter/advanced-filter";
import StandALoneContext from "../../common/components/context/StandAloneContext";
import { FileTypeDisplayName, StandAloneActions, Status, IsProduction } from "../../models/StandAloneData";
import YearEndBoxValueModel, { CompanyImportData } from "../FileDetail/YearEnd/YearEndBoxValueModel";
import { ErrorColumnsDefinitions } from "../FileDetail/Error/ErrorColumnsDefinitions";
import { Button } from "@ceridianhcm/components";
import logger from '../../logger';
import { useYearEndColumnsDefinitions } from "./YearEndColumnDefinitions";
import { downloadCsv } from "../FileDetail/YearEnd/YearEndBoxValueColumnsDefinition";

const defaultFiltersYearEnd: FilterValue[] = [];

const textResourcesYearEnd = {
    tableDescription: "Year-End File Processing",
    tableNoDataText: "No Files Found",
    tableAddItemText: "",
};

const statusValuesYearEnd = [
    { id: '0', title: 'Validating' },
    { id: '1', title: "Aggregating" },
    { id: '2', title: "Transmitting" },
    { id: '3', title: "Completed" },
    { id: '4', title: "Error" },
    { id: '5', title: "TransmissionError" }
];
enum Environment {
    Prod = '1',
    Test = '0',
}

const environmentValuesYearEnd = [
    { id: Environment.Prod, title: 'Prod' },
    { id: Environment.Test, title: 'Test' },
];

interface IReducerPropertiesYearEnd {
    formValues: any | null,
    page: number,
    currentMenu: NavbarItem[],
    progressMessage: string,
    loading: boolean
};

interface IReducerActionYearEnd {
    type: string,
    value?: any | null
};

function dataReducer(state: IReducerPropertiesYearEnd, action: IReducerActionYearEnd): IReducerPropertiesYearEnd {
    let result: IReducerPropertiesYearEnd = {
        formValues: state.formValues,
        page: state.page,
        currentMenu: state.currentMenu,
        progressMessage: state.progressMessage,
        loading: state.loading
    };

    switch (action.type) {
        case 'pageLoadedYearEnd':
            result.loading = false;
            break;
        case 'toggleFilterYearEnd':
            result.currentMenu = [...result.currentMenu];
            result.currentMenu[0].Active = !result.currentMenu[0].Active;
            result.loading = false;
            break;
        case 'default':
            break;
    }
    return result;
}

const groupRowsByImportIdAndStatus = (rows: any[] = []): any[] => {
    if (!rows.length) return [];

    const groups = new Map<number, any[]>();
    const order: number[] = [];

    rows.forEach((row: any) => {
        const id = row.importId;
        let group = groups.get(id);

        if (!group) {
            group = [];
            groups.set(id, group);
            order.push(id);
        }

        group.push(row);
    });

    const GROUPABLE_STATUSES = [Status.Completed, Status.Transmitting];
    const result: any[] = [];
    order.forEach(id => {
        const group = groups.get(id);

        // Safety guard to avoid non-null assertion.
        if (!group || group.length === 0) return;

        const firstRow = group[0];
        const shouldGroup = GROUPABLE_STATUSES.includes(Number(firstRow.T4status ?? firstRow.status));

        if (shouldGroup && group.length > 1) {
            result.push({
                ...firstRow,
                _isParent: true,
                _children: group
            });
        } else {
            result.push(...group);
        }
    });

    return result;
};

const YearEndProcessing: React.FC<{ rowsPerPage?: any }> = ({
    rowsPerPage = 10,
}) => {

    const { state, dispatch: contextDispatch } = useContext(StandALoneContext);
    const [showModal, setShowModalYearEnd] = useState(false);
    const [loading, setLoadingYearEnd] = useState(true);
    const [companyIds, setCompanyIdsYearEnd] = useState([]);
    const [companyNamesLst, setCompanyNamesYearEnd] = useState([]);
    const [fileTypeOptions, setFileTypeOptions] = useState<{ id: string; title: string }[]>([]);
    const [allFileTypeIds, setAllFileTypeIds] = useState<string[]>([]);
    const [fileTypeLookup, setFileTypeLookup] = useState<Record<string, string>>({});
    const [containerTypeOptions, setContainerTypeOptions] = useState<{ id: string; title: string }[]>([]);
    const [allContainerTypeIds, setAllContainerTypeIds] = useState<string[]>([]);
    const [containerTypeLookup, setContainerTypeLookup] = useState<Record<string, string>>({});
    const [submissionTypeOptions, setSubmissionTypeOptions] = useState<{ id: string; title: string }[]>([]);
    const [allSubmissionTypeIds, setAllSubmissionTypeIds] = useState<string[]>([]);
    const [submissionTypeLookup, setSubmissionTypeLookup] = useState<Record<string, string>>({});
    const [errors, setErrors] = useState(false);
    const [fileTypesLoaded, setFileTypesLoaded] = useState(false);
    const [loadingError, setLoadingError] = useState(true);
    const [errorData, setErrorData] = useState<any[]>([]);
    const [localRowsPerPage, setLocalRowsPerPage] = useState<number>(rowsPerPage ?? 10);
    const [importFilesData, setImportFilesDataYearEnd] = useState(state.importFilesData);
    const [currentFile, setCurrentFile] = useState<any>(null);

    const processedData = useMemo(() => {
        const rows = importFilesData?.output;
        return groupRowsByImportIdAndStatus(rows);
    }, [importFilesData?.output]);

    const [reducerState, dispatch] = useReducer(dataReducer, {
        formValues: null,
        page: state.serverPage,
        progressMessage: "Loading...",
        loading: true,
        currentMenu: [
            new NavbarItem({
                active: false,
                title: 'Filter',
                icon: 'filter',
                onClick: () => {
                    dispatch({
                        type: 'toggleFilterYearEnd',
                        value: null
                    });
                }
            }),
        ],
    });

    const GetImportStatus = async (currentFile: CompanyImportData) => {
        // Reset states when clicking a new file
        setErrors(false);
        setErrorData([]);
        setLoadingError(true);
        setShowModalYearEnd(false);

        const {importDetailsId, importId } = currentFile;
        try {
            const response = await GetImportById(importId);
            
            if (response.status === Status.Error || response.status === Status.TransmissionError) {
                await getValidationErrors(importId);
                setErrors(true);
            }
            else {
                contextDispatch({
                    type: StandAloneActions.SET_IMPORT_DETAIL,
                    payload: {
                        ...state,
                        importDetailId:importDetailsId,
                    },
                });
                setCurrentFile(currentFile);
                setShowModalYearEnd(true);
            }
        } catch (error) {
            return new Error(error)
        }
    };

    const YearEndColumns = useYearEndColumnsDefinitions(GetImportStatus, fileTypeLookup, submissionTypeLookup);

    const CreateExport = async () => {
        try {
            const yearEndResponse = await GetYearEndImportFiles(buildFiltersYearEnd(0));
            const excludedYearEndColumns = ['collectorId'];
            const yearEndRows = groupRowsByImportIdAndStatus(yearEndResponse?.output || []);

            if (yearEndRows.length === 0) {
                logger.warn("No data available for export.");
                return;
            }

            const yearEndCols = [
                {
                    field: "importId",
                    label: "Import ID",
                    cellTemplate: (row) => row.importId,
                },
                {
                    field: "companyName",
                    label: "Company",
                    cellTemplate: (row) => row.T4companyName || row.companyName,
                },
                {
                    field: "companyId",
                    label: "Company Identifier",
                    cellTemplate: (row) => (row.T4companyId || row.companyId)?.slice(1),
                },
                {
                    field: "fileName",
                    label: "Filename",
                    cellTemplate: (row) => row.T4fileName || row.fileName,
                },
                {
                    field: "fileType",
                    label: "Type",
                    cellTemplate: (row) => {
                        const fileTypeValue = row.T4fileType ?? row.fileType;
                        const fileTypeId = String(fileTypeValue);
                        return fileTypeLookup[fileTypeId] || FileTypeDisplayName[fileTypeValue] || 'Unknown';
                    },
                },
                {
                    field: "status",
                    label: "Status",
                    cellTemplate: (row) => {
                        const statusMap = {
                            '0': 'Validating',
                            '1': 'Aggregating',
                            '2': 'Transmitting',
                            '3': 'Completed',
                            '4': 'Error',
                            '5': 'TransmissionError'
                        };
                        const statusValue = row.T4status ?? row.status;
                        return statusMap[statusValue] || statusValue;
                    },
                },
                {
                    field: "submissionType",
                    label: "Submission Type",
                    cellTemplate: (row) => {
                        const submissionTypeId = String(row.submissionType ?? '');
                        if (submissionTypeId === '0') return '';
                        return submissionTypeLookup[submissionTypeId] || submissionTypeId;
                    },
                },
                {
                    field: "isProduction",
                    label: "Environment",
                    cellTemplate: (row) => IsProduction[row.T4isProduction ?? row.isProduction],
                },
                {
                    field: "createdOn",
                    label: "Created",
                    cellTemplate: (rowData) => {
                        const createdOnValue = rowData.T4createdOn ?? rowData.createdOn;
                        if (!createdOnValue) return "";
                        const date = new Date(createdOnValue);
                        return date.toLocaleDateString();
                    },
                },
            ];

            const yearEndHeaders = yearEndCols
                .filter(col => !excludedYearEndColumns.includes(col.field))
                .map(col => col.label);

            const yearEndCsvRows: string[] = [];
            yearEndCsvRows.push(yearEndHeaders.join(','));

            yearEndRows.forEach((row) => {
                const rowValuesYearEnd = yearEndCols
                    .filter(col => !excludedYearEndColumns.includes(col.field))
                    .map(col => {
                        let cellValue = col.cellTemplate(row);
                        if (typeof cellValue === 'string') {
                            cellValue = `"${cellValue.replace(/"/g, '""')}"`;
                        }
                        return cellValue;
                    });
                yearEndCsvRows.push(rowValuesYearEnd.join(','));
            });

            const now = new Date();
            const formattedDate = now.toISOString().replace(/[:.-]/g, '').slice(0, 15);
            const csvfileName = `YearEndProcessing_${formattedDate}.csv`;
            const screenName = "YearEnd Processing";
            const csvContent = yearEndCsvRows.join('\n');

            await downloadCsv(csvfileName, csvContent, screenName);
        } catch (error) {
            setErrors(true);
            logger.error("Error fetching or exporting data:", error);
        }
    };

    const exportErrorData = async () => {
        try {
            if (errorData.length === 0) {
                logger.warn("No error data available for export.");
                return;
            }

            const errorHeadersYE = ["Error", "Message"];
            const errorCsvRowsYE: string[] = [errorHeadersYE.join(",")];

            errorData.forEach(err => {
                const message = err.message ? err.message.replace(/"/g, '""') : '';
                errorCsvRowsYE.push(`"Error","${message}"`);
            });

            const now = new Date();
            const formattedDate = now.toISOString().replace(/[:.-]/g, '').slice(0, 15);
            const csvfileName = `YearEndProcessing_Errors_${formattedDate}.csv`;
            const screenName = "YearEnd Processing";
            const csvContent = errorCsvRowsYE.join('\n');

            await downloadCsv(csvfileName, csvContent, screenName);
        } catch (error) {
            logger.error("Error exporting error data:", error);
        }
    };

    let FilterFieldsYearEnd: FilterField[] = [
        new FilterField({
            type: FilterFieldTypes.multiple_select,
            name: 'companyName',
            title: 'Company Name',
            options: companyNamesLst
        }),
        new FilterField({
            type: FilterFieldTypes.multiple_select,
            name: 'companyId',
            title: 'Company ID ',
            options: companyIds
        }),
        new FilterField({
            type: FilterFieldTypes.multiple_select,
            name: 'fileType',
            title: 'File Type',
            options: fileTypeOptions
        }),
        new FilterField({
            type: FilterFieldTypes.multiple_select,
            name: 'isProduction',
            title: 'Prod/Test',
            options: environmentValuesYearEnd
        }),
        new FilterField({
            type: FilterFieldTypes.multiple_select,
            name: 'status',
            title: 'Status',
            options: statusValuesYearEnd
        }),
        new FilterField({
            type: FilterFieldTypes.multiple_select,
            name: 'containerType',
            title: 'Container Type',
            options: containerTypeOptions
        }),
        new FilterField({
            type: FilterFieldTypes.multiple_select,
            name: 'submissionType',
            title: 'Submission Type',
            options: submissionTypeOptions
        })
    ];

    async function getValidationErrors(importId) {
        try {
            const resp = await GetValidationErrorsByImportId(importId);
          
            if (resp && resp.length > 0) {
                setErrorData(resp);
            } else {
                setErrorData([]);
            }
            setLoadingError(false);
        } catch (error) {
            setLoadingError(false);
            logger.error("Error fetching validation errors:", error);
        }
    };
    const fileName = state?.fileName;
    const getCompanyListYearEnd = async () => {
        let companyIDSYearEnd = new Array();
        let companyNamesListYearEnd = new Array();
        try {
            const data = await GetCompanyList();
            if (data && data.length > 0) {
                data.forEach(x => {
                    companyIDSYearEnd.push({ id: x.companyId, title: x.companyId.slice(1) });
                    companyNamesListYearEnd.push({ id: x.companyId, title: x.companyId.slice(1) + ' ' + x.companyName });
                });
            }
            setCompanyIdsYearEnd(companyIDSYearEnd);
            setCompanyNamesYearEnd(companyNamesListYearEnd);
        } catch (error) {
            return new Error(error)
        }
    };

    const getContainerTypesYearEnd = async () => {
        try {
            const data = await GetYearEndContainerTypes();
            if (data && data.length > 0) {
                const options = data.filter(ct => String(ct.id) !== '0').map(ct => ({ id: String(ct.id), title: ct.description }));
                const allIds = data.map(ct => String(ct.id));
                const lookup = data.reduce((acc, ct) => {
                    acc[String(ct.id)] = ct.description;
                    return acc;
                }, {} as Record<string, string>);
                setContainerTypeOptions(options);
                setAllContainerTypeIds(allIds);
                setContainerTypeLookup(lookup);
            }
        } catch (error) {
            return new Error(error);
        }
    };

    const getSubmissionTypesYearEnd = async () => {
        try {
            const data = await GetYearEndSubmissionTypes();
            if (data && data.length > 0) {
                const options = data.filter(st => String(st.id) !== '0').map(st => ({ id: String(st.id), title: st.description }));
                const allIds = data.map(st => String(st.id));
                const lookup = data.reduce((acc, st) => {
                    acc[String(st.id)] = st.description;
                    return acc;
                }, {} as Record<string, string>);
                setSubmissionTypeOptions(options);
                setAllSubmissionTypeIds(allIds);
                setSubmissionTypeLookup(lookup);
            }
        } catch (error) {
            return new Error(error);
        }
    };

    const buildFiltersYearEnd = (pageSize: number) => ({
        page: state.serverPage,
        pageSize,
        companyName: state.filterData.companyName,
        companyId: state.filterData.companyId,
        fileType: state.filterData.fileType?.length ? state.filterData.fileType : allFileTypeIds,
        isProduction: state.filterData.isProduction,
        status: state.filterData.status,
        containerType: state.filterData.containerType?.length ? state.filterData.containerType : allContainerTypeIds,
        submissionType: state.filterData.submissionType?.length ? state.filterData.submissionType : allSubmissionTypeIds,
    });

    async function getImportFilesDataYearEnd(pageSize?: number) {
        setLoadingYearEnd(true);
        try {
            const response = await GetYearEndImportFiles(buildFiltersYearEnd(pageSize ?? localRowsPerPage ?? 10));
            setImportFilesDataYearEnd(response);
            setLoadingYearEnd(false);
        } catch (error) {
            setLoadingYearEnd(false);
            return new Error(error);
        }
    }

    const applyFiltersYearEnd = async (filtersYearEnd: FilterValue[] | null) => {
        let filterValuesYearEnd: any = {};
        if (filtersYearEnd !== null) {
            filtersYearEnd.forEach((filter) => {
                switch (filter.FieldName) {
                    case 'companyId':
                        filterValuesYearEnd["companyId"] = filter.Value as string[];
                        break;
                    case 'companyName':
                        filterValuesYearEnd["companyName"] = filter.Value as string[];
                        break;
                    case 'fileType':
                        filterValuesYearEnd["fileType"] = filter.Value as string[];
                        break;
                    case 'isProduction':
                        filterValuesYearEnd["isProduction"] = filter.Value as string[];
                        break;
                    case 'status':
                        filterValuesYearEnd["status"] = filter.Value as string[];
                        break;
                    case 'containerType':
                        filterValuesYearEnd["containerType"] = filter.Value as string[];
                        break;
                    case 'submissionType':
                        filterValuesYearEnd["submissionType"] = filter.Value as string[];
                        break;
                }
            });
        }
        contextDispatch({
            type: StandAloneActions.SET_FILTER_DATA,
            payload: {
                pageIdx: 0,
                filterValues: filterValuesYearEnd
            }
        });
    }
    useEffect(() => {
        // Only fetch data after the file types request has completed (regardless of result)
        if (fileTypesLoaded &&
            (state.serverPageChange !== null || state.serverPage !== null || state.applyFilterClick !== null)) {
            getImportFilesDataYearEnd();
        }
    }, [state.serverPageChange, state.serverPage, state.applyFilterClick, fileTypesLoaded]);

    const goToNextPage = () => {
        contextDispatch({
            type: StandAloneActions.SET_MAIN_PAGE,
            payload: { ...state },
        });
    };
    const getFileTypesYearEnd = async () => {
        try {
            const data = await GetImportFileTypes("YearEnd");
            if (data && data.length > 0) {
                const options = data.map(ft => ({ id: String(ft.fileType), title: ft.fileDescription }));
                const allIds = data.map(ft => String(ft.fileType));
                const lookup = data.reduce((acc, ft) => {
                    acc[String(ft.fileType)] = ft.fileDescription;
                    return acc;
                }, {} as Record<string, string>);

                setFileTypeOptions(options);
                setAllFileTypeIds(allIds);
                setFileTypeLookup(lookup);
            }
        } catch (error) {
            return new Error(error);
        } finally {
            setFileTypesLoaded(true);
        }
    };

    useEffect(() => {
        const loadInitialData = async () => {
            await getCompanyListYearEnd();
            await getContainerTypesYearEnd();
            await getSubmissionTypesYearEnd();
            await getFileTypesYearEnd();
            dispatch({
                type: 'pageLoadedYearEnd',
                value: null
            });
        };
        loadInitialData();
    }, []);

    const exportMenuItem = new NavbarItem({
        active: false,
        title: 'Export',
        icon: 'export',
        onClick: () => { void CreateExport(); }
    });

    const exportErrorMenuItem = new NavbarItem({
        active: false,
        title: 'Export',
        icon: 'export',
        onClick: () => { void exportErrorData(); }
    });

    return (
        <>
            <ProgressOverlay message={reducerState.progressMessage} visible={reducerState.loading} testId="progressOverlay" />
            <Panel fullWidth={true}>
                <Panel.Block>
                    <Navbar
                        startItems={reducerState.currentMenu}
                        endItems={!errors ? [exportMenuItem] : [exportErrorMenuItem]}
                        testId="primaryNavBar"
                    />
                </Panel.Block>
                <Panel.Block visible={reducerState.currentMenu[0].Active} focused={true}>
                    <AdvancedFilter
                        filters={FilterFieldsYearEnd}
                        defaultFilters={defaultFiltersYearEnd}
                        applyFilters={(filters) => { applyFiltersYearEnd(filters); }}
                        testId="primaryAdvancedFilter"
                        requiresAtLeastOne={true}
                    />
                </Panel.Block>
                {!errors &&
                    <Panel.Block>
                        <TableV
                            testId={"canada-standalone-ui"}
                            columns={YearEndColumns}
                            isLoading={loading}
                            dataLength={importFilesData?.count}
                            data={processedData}
                            dataKey={"Canada"}
                            description={textResourcesYearEnd.tableDescription}
                            noDataText={textResourcesYearEnd.tableNoDataText}
                            addItemText={textResourcesYearEnd.tableAddItemText}
                            pageIndex={state.serverPage}
                            getNextPageDetailsFromAPI={goToNextPage}
                            rowsPerPage={localRowsPerPage}
                            onRowsPerPageChange={(newSize) => {
                                setLocalRowsPerPage(newSize);
                                getImportFilesDataYearEnd(newSize);
                            }}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            serverSide={true}
                            tableHeight="83vh"
                        />
                    </Panel.Block>
                }
                {errors &&
                    <Panel.Block>
                        <TableV
                            testId={"canada-standalone-ui-YearEnd-error"}
                            isLoading={loadingError}
                            columns={ErrorColumnsDefinitions()}
                            data={errorData}
                            dataKey={"Canada"}
                            dataLength={errorData?.length || 0}
                            description={"YearEnd Error Data"}
                            noDataText={"No validation errors found"}
                            addItemText={""}
                            tableHeight="200vh"
                            rowsPerPage={rowsPerPage}
                        />
                    </Panel.Block>
                }
            </Panel>
            {showModal && <YearEndBoxValueModel key={state.importDetailId} show={showModal} onClose={() => setShowModalYearEnd(false)} currentFile={currentFile} containerTypeLookup={containerTypeLookup}/> }
            {errors &&
                <Button label={"Close"} id={"CloseBtn"} fullWidth={true} onClick={() => {
                    setErrors(false)
                }} />
            }
        </>
    );
};

export default YearEndProcessing;